// SEQ-199: Top N with an "Other" row.
//
// Keeps the first N rows in the order given and folds the rest into one
// "Other" row whose value for each key is the sum of the folded rows (null
// when none of them had a value). N of 0 or less keeps nothing and folds
// everything; N at or above the row count returns the rows untouched with no
// Other row. The caller sorts first.
//
// SUMMING IS ONLY RIGHT FOR ADDITIVE KEYS. Percentages and ranks cannot be
// summed. `sumKeys` names the keys to sum; any other key on the Other row is
// absent (null). The grid passes only its dollar and hour measures.

import { measureOf, type TransformRow } from "./types.ts";

export const OTHER_ROW_ID = "__other__";

export interface TopNResult {
  rows: TransformRow[];
  /** How many input rows were folded into Other. 0 means there is no Other row. */
  foldedCount: number;
}

export function topNWithOther(
  rows: readonly TransformRow[],
  n: number,
  sumKeys: readonly string[],
  otherId: string = OTHER_ROW_ID,
): TopNResult {
  const keep = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : rows.length;
  if (keep >= rows.length) return { rows: [...rows], foldedCount: 0 };
  const kept = rows.slice(0, keep);
  const rest = rows.slice(keep);
  const values: Record<string, number | null> = {};
  for (const key of sumKeys) {
    let sum = 0;
    let seen = false;
    for (const row of rest) {
      const value = measureOf(row, key);
      if (value === null) continue;
      sum += value;
      seen = true;
    }
    values[key] = seen ? sum : null;
  }
  return { rows: [...kept, { id: otherId, values }], foldedCount: rest.length };
}
